import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'

function PracticeTheGreeks({ onNavigate }) {
  const [stockPrice, setStockPrice] = useState(150);
  const [daysToExpiry, setDaysToExpiry] = useState(30);
  const [selectedStrike, setSelectedStrike] = useState(null);
  const [hoveredCol, setHoveredCol] = useState(null);
  const [activeChallenge, setActiveChallenge] = useState(0);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [challengeFeedback, setChallengeFeedback] = useState(null);

  function calcPremium(strike, sp, dte) {
    const intrinsic = Math.max(0, strike - sp);
    const atmDist = Math.abs(sp - strike);
    const timeVal = atmDist < 5
      ? sp * 0.02 * Math.sqrt(dte / 30)
      : sp * 0.01 * Math.sqrt(dte / 30) * Math.exp(-atmDist / sp * 5);
    return parseFloat((intrinsic + timeVal).toFixed(2));
  }

  function calcRow(strike) {
    const moneyness = (stockPrice - strike) / stockPrice;
    const baseDelta = 0.5 + moneyness * 2;
    const timeFactor = Math.sqrt(Math.max(1, daysToExpiry) / 365);
    const delta = -(Math.max(0.01, Math.min(0.99, baseDelta * timeFactor)));
    const premium = calcPremium(strike, stockPrice, daysToExpiry);
    const theta = -(premium / Math.max(1, daysToExpiry)) * 0.7;
    const atmDist = Math.abs(stockPrice - strike) / stockPrice;
    const vega = 0.15 * Math.exp(-atmDist * 8) * Math.sqrt(daysToExpiry / 30);
    const gamma = 0.05 * Math.exp(-atmDist * 6) * (30 / Math.max(1, daysToExpiry));
    const oi = 1000 + (strike % 10 === 0 ? 2000 : 0) + (Math.abs(stockPrice - strike) < 5 ? 1500 : 0) + (strike % 5 === 0 && strike % 10 !== 0 ? 800 : 0) + ((strike * 7) % 500);
    return {
      strike,
      delta: parseFloat(delta.toFixed(3)),
      theta: parseFloat(theta.toFixed(3)),
      vega: parseFloat(vega.toFixed(3)),
      gamma: parseFloat(gamma.toFixed(4)),
      premium,
      oi,
      isATM: Math.abs(strike - stockPrice) <= 2.5,
      isITM: strike > stockPrice,
    };
  }

  const atmStrike = Math.round(stockPrice / 5) * 5;
  const chain = [-20,-15,-10,-5,0,5,10,15,20].map(d => calcRow(atmStrike + d));
  const selectedRow = selectedStrike ? chain.find(r => r.strike === selectedStrike) : null;

  const TIPS = {
    delta:   'Delta: How much this option moves per $1 stock movement. Also approximates probability of expiring in the money.',
    theta:   'Theta: Daily time decay. The option loses this amount in value each day. Sellers collect theta.',
    vega:    'Vega: Sensitivity to volatility. Option price changes by this amount per 1% IV change.',
    gamma:   'Gamma: Rate of delta change. High gamma near expiry means delta can shift quickly.',
    premium: 'Premium: The price of this option. As a seller you collect this amount per share (× 100 per contract).',
    oi:      'Open Interest: Number of active contracts at this strike. Above 500 = liquid. Below 500 = avoid.',
  };

  const CHALLENGES = [
    { title: 'Find the -0.15 Delta Sweet Spot', desc: 'Click the strike with delta closest to -0.15. This is the beginner sweet spot.', check: r => Math.abs(r.delta) >= 0.12 && Math.abs(r.delta) <= 0.18, success: r => `✓ Correct! ~${Math.round((1 - Math.abs(r.delta)) * 100)}% probability of expiring worthless. At $${r.premium} premium you collect $${(r.premium * 100).toFixed(0)} per contract.`, fail: 'Not quite. Look for delta closest to -0.15 in the OTM section below the stock price.' },
    { title: 'Gamma Spike Near Expiry', desc: 'Move the DTE slider to 7 days. What happens to Gamma?', autoDetect: () => daysToExpiry <= 10, success: () => '✓ Gamma spiked. This is why we never hold short options into the final week — delta can change dramatically with small price moves.' },
    { title: 'Find the Highest Open Interest', desc: 'Click the strike with the highest Open Interest. Why does this matter?', check: r => r.oi >= Math.max(...chain.map(c => c.oi)) - 50, success: () => '✓ High Open Interest means more buyers and sellers — better fills and tighter spreads. Always confirm OI above 500 before entering any position.', fail: 'Look for the strike with the highest number in the OI column.' },
    { title: 'Delta Increases as Price Falls', desc: 'Move the stock price down $10 from 150. Watch what happens to delta.', autoDetect: () => stockPrice <= 140, success: () => '✓ Delta increased as the stock dropped. This is delta in action — assignment risk increases as price falls toward your strike.' },
  ];

  const currentChallenge = activeChallenge < CHALLENGES.length ? CHALLENGES[activeChallenge] : null;

  const handleRowClick = row => {
    setSelectedStrike(row.strike);
    if (!currentChallenge || currentChallenge.autoDetect) return;
    if (currentChallenge.check(row)) {
      setChallengeFeedback({ ok: true, msg: currentChallenge.success(row) });
      setTimeout(() => { setCompletedChallenges(p => [...p, activeChallenge]); setActiveChallenge(p => p + 1); setChallengeFeedback(null); }, 2200);
    } else {
      setChallengeFeedback({ ok: false, msg: currentChallenge.fail });
    }
  };

  useEffect(() => {
    if (!currentChallenge?.autoDetect) return;
    if (currentChallenge.autoDetect()) {
      setChallengeFeedback({ ok: true, msg: currentChallenge.success() });
      const t = setTimeout(() => { setCompletedChallenges(p => [...p, activeChallenge]); setActiveChallenge(p => p + 1); setChallengeFeedback(null); }, 2200);
      return () => clearTimeout(t);
    }
  }, [stockPrice, daysToExpiry, activeChallenge]); // eslint-disable-line

  const dteColor = daysToExpiry > 21 ? V.sage : daysToExpiry >= 7 ? V.gold : V.rose;
  const dCol = d => { const a = Math.abs(d); return a <= 0.16 ? V.sage : a <= 0.30 ? V.gold : V.rose; };

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button onClick={() => onNavigate && onNavigate('chapter_ch05')} style={{ ...btnGhost, fontSize: '12px', padding: '6px 14px' }}>← Reading the Greeks</button>
        <button onClick={() => onNavigate && onNavigate('dashboard')} style={{ ...btnGhost, fontSize: '12px', padding: '6px 14px' }}>All Practice →</button>
      </div>
      <div style={{ ...eyebrow, marginBottom: '4px' }}>PRACTICE — THE GREEKS</div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, color: V.gold, margin: '0 0 6px' }}>Practice the Greeks</h2>
      <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.muted, fontStyle: 'italic', lineHeight: 1.7, marginBottom: '24px' }}>
        Move the sliders. Watch every Greek update in real time. This is how options are actually priced.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,0.8fr)', gap: '20px', alignItems: 'start' }}>

        {/* LEFT — Options Chain */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ ...mono, fontSize: '28px', color: V.gold, fontWeight: 700 }}>${stockPrice}</div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted }}>ATM: <span style={{ color: V.gold }}>${atmStrike}</span></div>
          </div>

          <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '52px 58px 80px 58px 64px 60px 54px 38px', padding: '7px 10px', background: V.bg3, borderBottom: `0.5px solid ${V.border}`, gap: '2px' }}>
              {[['strike','STRIKE',null],['delta','DELTA',TIPS.delta],['theta','THETA',TIPS.theta],['vega','VEGA',TIPS.vega],['gamma','GAMMA',TIPS.gamma],['premium','PREM',TIPS.premium],['oi','OI',TIPS.oi],['type','TYPE',null]].map(([k,l,tip]) => (
                <div key={k} style={{ position: 'relative' }} onMouseEnter={() => tip && setHoveredCol(k)} onMouseLeave={() => setHoveredCol(null)}>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '9px', color: tip ? V.goldL : V.dim, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: tip ? 'help' : 'default', borderBottom: tip ? '1px dotted rgba(201,168,76,0.4)' : 'none' }}>{l}</span>
                  {hoveredCol === k && tip && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, width: '210px', background: '#18181c', border: `0.5px solid ${V.border}`, borderRadius: '8px', padding: '10px 12px', fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif", lineHeight: 1.65, marginTop: '4px' }}>{tip}</div>
                  )}
                </div>
              ))}
            </div>
            {/* Rows */}
            {chain.map(row => {
              const sel = selectedStrike === row.strike;
              const sweet = Math.abs(row.delta) >= 0.12 && Math.abs(row.delta) <= 0.18;
              const rowBg = sel ? 'rgba(201,168,76,0.12)' : row.isATM ? 'rgba(201,168,76,0.05)' : row.isITM ? 'rgba(196,122,122,0.05)' : 'transparent';
              return (
                <div key={row.strike} onClick={() => handleRowClick(row)}
                  style={{ display: 'grid', gridTemplateColumns: '52px 58px 80px 58px 64px 60px 54px 38px', padding: '8px 10px', cursor: 'pointer', borderBottom: `0.5px solid ${V.bg3}`, background: rowBg, borderLeft: `3px solid ${sel ? V.gold : row.isATM ? 'rgba(201,168,76,0.4)' : 'transparent'}`, gap: '2px', alignItems: 'center' }}>
                  <span style={{ ...mono, fontSize: '12px', color: row.isATM ? V.goldL : V.text, fontWeight: row.isATM ? 700 : 500 }}>${row.strike}</span>
                  <span style={{ ...mono, fontSize: '12px', color: dCol(row.delta), fontWeight: sweet ? 700 : 400 }}>{row.delta.toFixed(2)}{sweet ? ' ★' : ''}</span>
                  <span style={{ ...mono, fontSize: '11px', color: V.rose }}>-${(Math.abs(row.theta) * 100).toFixed(2)}/d</span>
                  <span style={{ ...mono, fontSize: '11px', color: '#4a9eff' }}>{row.vega.toFixed(3)}</span>
                  <span style={{ ...mono, fontSize: '11px', color: '#9a7ac4' }}>{row.gamma.toFixed(4)}</span>
                  <span style={{ ...mono, fontSize: '12px', color: V.gold }}>${row.premium}</span>
                  <span style={{ ...mono, fontSize: '11px', color: row.oi >= 500 ? V.sage : V.rose }}>{row.oi >= 1000 ? (row.oi / 1000).toFixed(1) + 'K' : row.oi}</span>
                  <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '9px', color: row.isATM ? V.gold : row.isITM ? V.rose : V.dim }}>{row.isATM ? 'ATM' : row.isITM ? 'ITM' : 'OTM'}</span>
                </div>
              );
            })}
          </div>

          {/* Greek detail panel */}
          {selectedRow && (() => {
            const r = selectedRow;
            const sweet = Math.abs(r.delta) >= 0.12 && Math.abs(r.delta) <= 0.18;
            return (
              <div style={{ background: V.bg2, border: `1px solid ${V.border}`, borderRadius: '10px', padding: '20px', marginTop: '16px' }}>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '14px' }}>
                  Strike ${r.strike} — Greek Breakdown
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { label: 'Delta', val: r.delta.toFixed(3), color: dCol(r.delta), extra: sweet ? '★ Beginner sweet spot' : null, body: `For every $1 stock move this option moves $${(Math.abs(r.delta) * 100).toFixed(0)}\n\nProbability of expiring ITM: ~${Math.round(Math.abs(r.delta)*100)}%\nProbability of expiring worthless: ~${Math.round((1-Math.abs(r.delta))*100)}%\n\nAt 0.15 delta there is approximately an 85% probability this option expires worthless — you keep the premium.` },
                    { label: 'Theta', sub: 'Time Decay', val: `-$${(Math.abs(r.theta)*100).toFixed(2)}/day`, color: V.rose, body: `This option loses $${(Math.abs(r.theta)*100).toFixed(2)} in value every single day.\n\nIf you are the SELLER — theta works FOR you. You collect this decay daily.\n\nOver ${daysToExpiry} days: $${(Math.abs(r.theta)*daysToExpiry*100).toFixed(0)} total theta available.` },
                    { label: 'Vega', sub: 'Volatility Sensitivity', val: `$${(r.vega*100).toFixed(2)} per 1% IV`, color: '#4a9eff', body: `If implied volatility increases by 1% — this option's price increases by $${(r.vega*100).toFixed(2)}.\n\nAs a seller — rising volatility works against you temporarily. Falling volatility works for you.\n\nThis is why we check VIX and IV Rank before entering.` },
                    { label: 'Gamma', sub: "Delta's Rate of Change", val: r.gamma.toFixed(4), color: '#9a7ac4', body: `Gamma tells you how fast Delta is changing.\n\nHigh gamma means Delta is accelerating — your risk can change quickly.\n\nAt 30-45 DTE gamma is low — changes are gradual and manageable.\n\nNear expiration gamma spikes — this is why we close or roll before the final 2 weeks.` },
                  ].map(({ label, sub, val, color, extra, body }) => (
                    <div key={label} style={{ background: V.bg3, borderRadius: '8px', padding: '12px', border: `0.5px solid ${V.border}` }}>
                      <div style={{ ...mono, fontSize: '16px', color, fontWeight: 700, marginBottom: '2px' }}>{val}</div>
                      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.gold, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: extra ? '4px' : '8px' }}>{label}{sub ? ` — ${sub}` : ''}</div>
                      {extra && <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.sage, marginBottom: '6px' }}>{extra}</div>}
                      <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, lineHeight: 1.7, whiteSpace: 'pre-line' }}>{body}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>

        {/* RIGHT — Controls + Challenges */}
        <div>
          {/* Stock price slider */}
          <div style={{ ...card, marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted }}>Stock Price</span>
              <span style={{ ...mono, fontSize: '22px', color: V.gold, fontWeight: 700 }}>${stockPrice}</span>
            </div>
            <input type="range" min={100} max={200} step={1} value={stockPrice} onChange={e => setStockPrice(parseInt(e.target.value))} style={{ width: '100%', accentColor: V.gold, cursor: 'pointer' }} />
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.dim, marginTop: '8px', lineHeight: 1.8 }}>
              {stockPrice > 155
                ? '• OTM puts move further OTM\n• Delta decreases (safer)\n• Premium decreases\n• Probability of profit increases'
                : stockPrice < 145
                ? '• OTM puts approach ATM\n• Delta increases (riskier)\n• Premium increases\n• Probability of profit decreases'
                : '• Price near ATM — watch delta closely'}
            </div>
          </div>

          {/* DTE slider */}
          <div style={{ ...card, marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted }}>Days to Expiration (DTE)</span>
              <span style={{ ...mono, fontSize: '22px', color: dteColor, fontWeight: 700 }}>{daysToExpiry}</span>
            </div>
            <input type="range" min={1} max={60} step={1} value={daysToExpiry} onChange={e => setDaysToExpiry(parseInt(e.target.value))} style={{ width: '100%', accentColor: V.gold, cursor: 'pointer' }} />
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: dteColor, marginTop: '6px' }}>
              {daysToExpiry > 21 ? '✓ Safe zone — theta and gamma are manageable' : daysToExpiry >= 7 ? '⚠ Monitor closely — gamma increasing' : '🚨 Gamma risk — high delta sensitivity'}
            </div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.dim, marginTop: '4px', fontStyle: 'italic' }}>This program targets 30-45 DTE for new CSP entries</div>
          </div>

          {/* Challenges */}
          <div style={{ ...card }}>
            <div style={{ ...eyebrow, marginBottom: '10px' }}>GREEK CHALLENGES</div>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {CHALLENGES.map((_, i) => (
                <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px', background: completedChallenges.includes(i) ? V.sage : i === activeChallenge ? V.gold : V.bg3, transition: 'background 0.3s' }} />
              ))}
            </div>
            <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.dim, marginBottom: '12px' }}>
              {completedChallenges.length} of {CHALLENGES.length} completed
            </div>

            {activeChallenge < CHALLENGES.length ? (
              <>
                <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.gold, fontWeight: 600, marginBottom: '6px' }}>
                  Challenge {activeChallenge + 1}: {CHALLENGES[activeChallenge].title}
                </div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: 1.7, marginBottom: '10px' }}>
                  {CHALLENGES[activeChallenge].desc}
                </p>
                {challengeFeedback && (
                  <div style={{ padding: '10px 12px', borderRadius: '7px', border: `1px solid ${challengeFeedback.ok ? V.sage : V.gold}`, background: challengeFeedback.ok ? 'rgba(122,171,138,0.1)' : 'rgba(201,168,76,0.08)', fontFamily: "'Jost',sans-serif", fontSize: '12px', color: challengeFeedback.ok ? V.sage : V.gold, lineHeight: 1.7 }}>
                    {challengeFeedback.msg}
                  </div>
                )}
              </>
            ) : (
              <div style={{ background: 'rgba(122,171,138,0.1)', border: `1px solid ${V.sage}`, borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                <div style={{ ...serif, fontSize: '20px', color: V.sage, marginBottom: '8px' }}>✓ Greeks Practice Complete</div>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted, lineHeight: 1.7, marginBottom: '14px' }}>
                  You understand how options are actually priced.<br />Next: Apply this in the Options Chain Simulator.
                </p>
                <button onClick={() => onNavigate && onNavigate('simulator')} style={{ ...btnGold, width: '100%', padding: '10px', fontSize: '12px' }}>
                  Open Options Chain Simulator →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PracticeTheGreeks
